/* ──────────────────────  MenuManager.jsx  ────────────────────── */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Card, CardContent, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  Stack, Typography, IconButton, Divider, Switch, FormControlLabel,
  Paper, InputAdornment, Chip
} from '@mui/material';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import {
  Add, Delete, Edit, RestaurantMenu
} from '@mui/icons-material';

// === LUXURY THEME ===
const theme = createTheme({
  palette: {
    primary: { main: '#d4af37' },
    secondary: { main: '#b8932b' },
    background: { default: '#ffffff', paper: '#ffffff' },
    text: { primary: '#1a1a1a', secondary: '#555555' },
  },
  typography: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    h4: { fontWeight: 800, color: '#1a1a1a' },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
  },
  shape: { borderRadius: 28 },
});

// === LUXURY CARD ===
const LuxuryCard = styled(Card)(({ theme }) => ({
  background: '#ffffff',
  border: '1px solid rgba(212,175,55,0.3)',
  borderRadius: 28,
  padding: '28px 22px',
  boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 16px 40px rgba(0,0,0,0.12)',
    transform: 'translateY(-4px)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: 5,
    background: 'linear-gradient(90deg, #d4af37, #b8932b)',
    borderRadius: '28px 28px 0 0',
  },
}));

// === GOLD TEXT ===
const GoldText = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(90deg, #d4af37, #b8932b)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  fontWeight: 800,
}));

// === ACTION BUTTON ===
const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 16,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.95rem',
  padding: '12px 24px',
  boxShadow: '0 6px 16px rgba(212,175,55,0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 24px rgba(212,175,55,0.4)',
  },
}));

// === ITEM PAPER ===
const ItemPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 20,
  padding: '20px 24px',
  background: '#ffffff',
  border: '1px solid rgba(212,175,55,0.2)',
  boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
    borderColor: 'rgba(212,175,55,0.4)',
  },
}));

export default function MenuManager() {
  const [menu, setMenu] = useState([]);
  const [openAddCat, setOpenAddCat] = useState(false);
  const [openAddItem, setOpenAddItem] = useState(null); // categoryId
  const [openEditItem, setOpenEditItem] = useState(null); // item object
  const [newCatName, setNewCatName] = useState('');

  const token = localStorage.getItem('admin_token');
  const api = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  useEffect(() => {
    loadMenu();
  }, []);

  const loadMenu = async () => {
    try {
      const res = await axios.get(`${api}/admin-menu.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMenu(res.data.menu || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    }
  };

  const createCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      await axios.post(`${api}/admin-menu.php`, {
        type: 'category',
        name: newCatName,
        sort_order: menu.length + 1
      }, { headers: { Authorization: `Bearer ${token}` } });
      setNewCatName('');
      setOpenAddCat(false);
      loadMenu();
    } catch { }
  };

  const updateCategory = async (id, name) => {
    try {
      await axios.put(`${api}/admin-menu.php`, { type: 'category', id, name }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadMenu();
    } catch { }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Delete category and all items?')) return;
    try {
      await axios.delete(`${api}/admin-menu.php`, {
        data: { type: 'category', id },
        headers: { Authorization: `Bearer ${token}` }
      });
      loadMenu();
    } catch { }
  };

  const createItem = async (catId, formData) => {
    try {
      await axios.post(`${api}/admin-menu.php`, {
        type: 'item',
        category_id: catId,
        name: formData.name,
        price_half: parseFloat(formData.price_half) || 0,
        price_full: parseFloat(formData.price_full) || 0,
        price_per_piece: parseFloat(formData.price_per_piece) || 0,
        unit_type: formData.unit_type,
        is_available: formData.is_available ? 1 : 0
      }, { headers: { Authorization: `Bearer ${token}` } });
      setOpenAddItem(null);
      loadMenu();
    } catch { }
  };

  const updateItem = async (id, updated) => {
    try {
      await axios.put(`${api}/admin-menu.php`, {
        type: 'item',
        id,
        name: updated.name,
        price_half: parseFloat(updated.price_half) || 0,
        price_full: parseFloat(updated.price_full) || 0,
        price_per_piece: parseFloat(updated.price_per_piece) || 0,
        unit_type: updated.unit_type,
        is_available: updated.is_available ? 1 : 0
      }, { headers: { Authorization: `Bearer ${token}` } });
      setOpenEditItem(null);
      loadMenu();
    } catch { }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await axios.delete(`${api}/admin-menu.php`, {
        data: { type: 'item', id },
        headers: { Authorization: `Bearer ${token}` }
      });
      loadMenu();
    } catch { }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8f8f8', minHeight: '100vh' }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={5}>
            <Stack direction="row" spacing={2} alignItems="center">
              <RestaurantMenu sx={{ fontSize: 48, color: '#d4af37' }} />
              <Box>
                <GoldText variant="h4">Menu Management</GoldText>
                <Typography variant="body2" color="text.secondary">
                  Organize categories and dishes
                </Typography>
              </Box>
            </Stack>
            <ActionButton
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpenAddCat(true)}
              sx={{
                bgcolor: '#28a745',
                color: 'white',
                '&:hover': { bgcolor: '#218838' }
              }}
            >
              Add Category
            </ActionButton>
          </Stack>

          {menu.length === 0 ? (
            <Paper sx={{
              p: 8,
              textAlign: 'center',
              borderRadius: 4,
              bgcolor: '#ffffff',
              border: '1px dashed rgba(212,175,55,0.4)'
            }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No categories yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click "Add Category" to get started!
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={4}>
              {menu.map(cat => (
                <LuxuryCard key={cat.id}>
                  <CardContent sx={{ pt: 4 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                      <TextField
                        value={cat.name}
                        onChange={e => updateCategory(cat.id, e.target.value)}
                        variant="standard"
                        sx={{
                          fontSize: '1.6rem',
                          fontWeight: 700,
                          color: '#1a1a1a',
                          flex: 1,
                          mr: 2,
                          '& .MuiInputBase-input': { fontWeight: 700 }
                        }}
                        InputProps={{ disableUnderline: true }}
                      />
                      <IconButton
                        onClick={() => deleteCategory(cat.id)}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(220, 20, 60, 0.1)',
                          '&:hover': { bgcolor: 'rgba(220, 20, 60, 0.2)' },
                          color: '#c62828'
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Stack>

                    <Divider sx={{ mb: 3, borderColor: 'rgba(212,175,55,0.3)' }} />

                    <Stack spacing={2.5}>
                      {cat.items?.map(item => (
                        <ItemPaper key={item.id}>
                          <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                            alignItems: 'center'
                          }}>
                            <Typography sx={{
                              flex: '1 1 220px',
                              fontWeight: 600,
                              fontSize: '1.1rem',
                              color: '#1a1a1a'
                            }}>
                              {item.name}
                            </Typography>

                            <TextField
                              label="Half"
                              type="number"
                              value={item.price_half || ''}
                              size="small"
                              sx={{ width: 100 }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>
                              }}
                              disabled
                            />
                            <TextField
                              label="Full"
                              type="number"
                              value={item.price_full || ''}
                              size="small"
                              sx={{ width: 100 }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>
                              }}
                              disabled
                            />
                            <TextField
                              label="Per Piece"
                              type="number"
                              value={item.price_per_piece || ''}
                              size="small"
                              sx={{ width: 120 }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>
                              }}
                              disabled
                            />

                            <Chip
                              label={item.unit_type === 'half' ? 'Half' : item.unit_type === 'full' ? 'Full' : 'Per Piece'}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(212,175,55,0.15)',
                                color: '#d4af37',
                                fontWeight: 600,
                                border: '1px solid rgba(212,175,55,0.3)'
                              }}
                            />

                            <FormControlLabel
                              control={
                                <Switch
                                  checked={!!item.is_available}
                                  size="small"
                                  color="success"
                                />
                              }
                              label="Available"
                              disabled
                              sx={{ ml: 1 }}
                            />

                            <Stack direction="row" spacing={1}>
                              <IconButton
                                size="small"
                                onClick={() => setOpenEditItem(item)}
                                sx={{
                                  bgcolor: 'rgba(25, 118, 210, 0.1)',
                                  '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.2)' },
                                  color: '#1976d2'
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => deleteItem(item.id)}
                                sx={{
                                  bgcolor: 'rgba(220, 20, 60, 0.1)',
                                  '&:hover': { bgcolor: 'rgba(220, 20, 60, 0.2)' },
                                  color: '#c62828'
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Box>
                        </ItemPaper>
                      ))}

                      <ActionButton
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => setOpenAddItem(cat.id)}
                        size="small"
                        sx={{
                          alignSelf: 'flex-start',
                          borderColor: '#d4af37',
                          color: '#d4af37',
                          '&:hover': {
                            borderColor: '#b8932b',
                            color: '#b8932b',
                            bgcolor: 'rgba(212,175,55,0.05)'
                          }
                        }}
                      >
                        Add Item
                      </ActionButton>
                    </Stack>
                  </CardContent>
                </LuxuryCard>
              ))}
            </Stack>
          )}
        </Box>

        {/* ────── Add Category Dialog ────── */}
        <Dialog open={openAddCat} onClose={() => { setOpenAddCat(false); setNewCatName(''); }} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: '#fffdf0', color: '#d4af37', fontWeight: 700, fontSize: '1.3rem' }}>
            Add New Category
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              fullWidth
              label="Category Name"
              value={newCatName}
              onChange={e => setNewCatName(e.target.value)}
              autoFocus
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1, bgcolor: '#fffdf0' }}>
            <Button onClick={() => { setOpenAddCat(false); setNewCatName(''); }} sx={{ color: '#666' }}>
              Cancel
            </Button>
            <ActionButton onClick={createCategory} variant="contained">
              Create
            </ActionButton>
          </DialogActions>
        </Dialog>

        {/* ────── Add Item Dialog ────── */}
        <ItemDialog
          open={!!openAddItem}
          onClose={() => setOpenAddItem(null)}
          onSubmit={(data) => createItem(openAddItem, data)}
          title="Add Menu Item"
        />

        {/* ────── Edit Item Dialog ────── */}
        <ItemDialog
          open={!!openEditItem}
          item={openEditItem}
          onClose={() => setOpenEditItem(null)}
          onSubmit={(data) => updateItem(openEditItem.id, data)}
          title="Edit Menu Item"
        />
      </Box>
    </ThemeProvider>
  );
}

/* ────── Reusable Item Dialog (Add & Edit) ────── */
function ItemDialog({ open, item, onClose, onSubmit, title }) {
  const [form, setForm] = useState({
    name: '',
    price_half: '',
    price_full: '',
    price_per_piece: '',
    unit_type: 'full',
    is_available: true
  });

  useEffect(() => {
    if (open && item) {
      setForm({
        name: item.name || '',
        price_half: item.price_half || '',
        price_full: item.price_full || '',
        price_per_piece: item.price_per_piece || '',
        unit_type: item.unit_type || 'full',
        is_available: !!item.is_available
      });
    } else if (open && !item) {
      setForm({
        name: '',
        price_half: '',
        price_full: '',
        price_per_piece: '',
        unit_type: 'full',
        is_available: true
      });
    }
  }, [open, item]);

  const handleSubmit = () => {
    if (!form.name.trim()) return;
    onSubmit(form);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: '#fffdf0', color: '#d4af37', fontWeight: 700, fontSize: '1.3rem' }}>
        {title}
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          <TextField
            fullWidth
            label="Item Name"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            autoFocus
            variant="outlined"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Half Price"
              type="number"
              value={form.price_half}
              onChange={e => setForm({ ...form, price_half: e.target.value })}
              sx={{ flex: 1 }}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              variant="outlined"
            />
            <TextField
              label="Full Price"
              type="number"
              value={form.price_full}
              onChange={e => setForm({ ...form, price_full: e.target.value })}
              sx={{ flex: 1 }}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              variant="outlined"
            />
            <TextField
              label="Per Piece"
              type="number"
              value={form.price_per_piece}
              onChange={e => setForm({ ...form, price_per_piece: e.target.value })}
              sx={{ flex: 1 }}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              variant="outlined"
            />
          </Stack>

          <FormControl fullWidth variant="outlined">
            <InputLabel>Default Unit</InputLabel>
            <Select
              value={form.unit_type}
              onChange={e => setForm({ ...form, unit_type: e.target.value })}
              label="Default Unit"
            >
              <MenuItem value="half">Half</MenuItem>
              <MenuItem value="full">Full</MenuItem>
              <MenuItem value="piece">Per Piece</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={form.is_available}
                onChange={e => setForm({ ...form, is_available: e.target.checked })}
                color="success"
              />
            }
            label="Available for Order"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1, bgcolor: '#fffdf0' }}>
        <Button onClick={onClose} sx={{ color: '#666' }}>
          Cancel
        </Button>
        <ActionButton onClick={handleSubmit} variant="contained">
          {item ? 'Update' : 'Create'}
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
}